import { z } from 'zod';
import {
  WORKBOOK_PROFILE_MAX_ROWS,
  workbookProfileRequestSchema,
  workbookSourceSchema,
  type WorkbookDefinition,
  type WorkbookProfileRequest,
  type WorkbookRun,
} from '@erp/shared-types';
import { rpcClient } from '@/api/rpc/client';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import type { WorkbookAgentPort } from './useWorkbookAgentTools';
import { agentWorksheetResult } from './workbookAgentReads';

/** A table the agent names: a Source of the workbook, a Worksheet's Result, or a catalog candidate not added yet. */
const agentTableSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(100)
    .describe('What columns and keys below call this table.'),
  sourceId: z
    .uuid()
    .optional()
    .describe('A Source of this workbook, read as a run would read it.'),
  sheetId: z
    .uuid()
    .optional()
    .describe(
      'A Worksheet: its result as its tab shows it (newest current, else newest).',
    ),
  sourceJson: z
    .string()
    .max(64 * 1024)
    .optional()
    .describe(
      'A source not yet in the workbook (e.g. a workbook_catalog candidate), as a JSON source object.',
    ),
});
type AgentTable = z.infer<typeof agentTableSchema>;

export const profileToolParameters = z.object({
  tables: z.array(agentTableSchema).min(1).max(4),
  columns: workbookProfileRequestSchema.shape.columns,
  pairs: workbookProfileRequestSchema.shape.pairs,
  maxRows: z
    .number()
    .int()
    .positive()
    .max(WORKBOOK_PROFILE_MAX_ROWS)
    .default(WORKBOOK_PROFILE_MAX_ROWS),
});

/** A catalog candidate's source from its JSON, or why it can't be read. */
function catalogSource(
  name: string,
  sourceJson: string,
): WorkbookProfileRequest['tables'][number]['table'] | string {
  let raw: unknown;
  try {
    raw = JSON.parse(sourceJson);
  } catch {
    return `'${name}' sourceJson is not valid JSON.`;
  }
  const parsed = workbookSourceSchema.safeParse(raw);
  return parsed.success
    ? { kind: 'source', source: parsed.data }
    : `'${name}' sourceJson is not a source: ${z.prettifyError(parsed.error)}`;
}

/** Why a named table can't be profiled, or the table as the profile request reads it. */
function requestTable(
  table: AgentTable,
  definition: WorkbookDefinition,
  runs: readonly WorkbookRun[],
): WorkbookProfileRequest['tables'][number]['table'] | string {
  const given = [table.sourceId, table.sheetId, table.sourceJson].filter(
    (value) => value !== undefined,
  );
  if (given.length !== 1)
    return `Give '${table.name}' exactly one of sourceId, sheetId or sourceJson.`;
  if (table.sourceJson) return catalogSource(table.name, table.sourceJson);
  if (table.sourceId) {
    const source = definition.sources.find(({ id }) => id === table.sourceId);
    return source
      ? { kind: 'source', source }
      : `This workbook has no source with id ${table.sourceId}.`;
  }
  const read = table.sheetId
    ? agentWorksheetResult(runs, definition, table.sheetId, 0)
    : null;
  return read
    ? { kind: 'result', runId: read.runId, sheetId: read.sheetId }
    : `No run has calculated worksheet ${table.sheetId} yet. Run through it first.`;
}

/**
 * Resolves the tables the agent names and builds the profile request, or the
 * first reason one can't be read.
 */
export function agentProfileRequest(
  input: z.infer<typeof profileToolParameters>,
  definition: WorkbookDefinition,
  runs: readonly WorkbookRun[],
):
  { ok: true; request: WorkbookProfileRequest } | { ok: false; error: string } {
  const tables: WorkbookProfileRequest['tables'] = [];
  for (const table of input.tables) {
    const resolved = requestTable(table, definition, runs);
    if (typeof resolved === 'string') return { ok: false, error: resolved };
    tables.push({ name: table.name, table: resolved });
  }
  return {
    ok: true,
    request: {
      tables,
      columns: input.columns,
      pairs: input.pairs,
      maxRows: input.maxRows,
    },
  };
}

/** The on-page agent's Data profile: column statistics and key matches, never rows. */
export function useWorkbookAgentProfileTool(
  workbookId: string,
  port: WorkbookAgentPort,
) {
  return useFrontendTool({
    name: 'profile_workbook_data',
    description:
      'Profile sources and worksheet results before joining them, without reading their rows: per column the row count, blanks, errors, distinct values, whether it is unique, its most frequent values, min/max and value lengths; per key pair (left and right, each one or more columns joined with \'|\', or a formula such as "=UPPER([@Code])") how many keys match in each direction, repeated keys (a lookup on them fails the run) and a few unmatched values. Use it to test a join hypothesis: a 1-character code meeting a 5-character one shows as a 0% match with differing lengths. Reads at most maxRows rows of each source (truncated says it had more); a worksheet result is profiled whole.',
    parameters: profileToolParameters,
    readOnlyHint: true,
    awaitable: true,
    execute: async (input) => {
      const built = agentProfileRequest(
        input,
        port.snapshot().definition,
        input.tables.some((table) => table.sheetId)
          ? await rpcClient.workbooks.runs({ workbookId })
          : [],
      );
      if (!built.ok) return built;
      return rpcClient.workbooks.profile({
        workbookId,
        request: built.request,
      });
    },
  });
}
